/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 版本比较工具
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-01-15 10:27:38
 */

/**
 * 比较版本号
 * @param version1 版本号1 (如 "1.3.0")
 * @param version2 版本号2 (如 "1.2.9")
 * @returns 1: version1 > version2, 0: version1 = version2, -1: version1 < version2
 */
export function compareVersions(version1: string, version2: string): number {
  const v1Parts = version1.split('.').map(Number)
  const v2Parts = version2.split('.').map(Number)

  const maxLength = Math.max(v1Parts.length, v2Parts.length)

  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0
    const v2Part = v2Parts[i] || 0

    if (v1Part > v2Part) return 1
    if (v1Part < v2Part) return -1
  }

  return 0
}

/**
 * 检查版本是否大于等于指定版本
 * @param currentVersion 当前版本
 * @param targetVersion 目标版本
 * @returns 是否大于等于
 */
export function isVersionGreaterOrEqual(
  currentVersion: string,
  targetVersion: string
): boolean {
  return compareVersions(currentVersion, targetVersion) >= 0
}

/**
 * 获取客户端版本号
 * @returns 客户端版本号，如果不是客户端环境返回 null
 */
export function getClientVersion(): string | null {
  if (typeof window !== 'undefined' && (window as any)?.clientInfo?.version) {
    if ((window as any).clientInfo.version.includes('-')) {
      return (window as any).clientInfo.version.split('-')[0]
    }
    return (window as any).clientInfo.version
  }
  return null
}

/**
 * 检查是否支持新的下载功能（版本 >= 1.3.0）
 * @returns 是否支持新下载功能
 */
export function supportsNewDownloadFeature(version: string = '1.3.0'): boolean {
  const clientVersion = getClientVersion()
  if (!clientVersion) return false
  return isVersionGreaterOrEqual(clientVersion, version)
}

/**
 * 检查当前版本是否在某两个版本之间
 * @param minVersion 最小版本
 * @param maxVersion 最大版本
 * @returns 是否在两个版本之间
 */
export function isVersionBetween(
  minVersion: string,
  maxVersion: string
): boolean {
  const clientVersion = getClientVersion()
  if (!clientVersion) return false
  return (
    isVersionGreaterOrEqual(clientVersion, minVersion) &&
    isVersionLessThan(maxVersion)
  )
}
/**
 * 检查当前版本是否小于指定版本
 * @param targetVersion 目标版本
 * @returns 是否小于
 */
export function isVersionLessThan(targetVersion: string): boolean {
  const clientVersion = getClientVersion()
  if (!clientVersion) return false
  return compareVersions(clientVersion, targetVersion) < 0
}
